/**
 * Inline SVG icons for the sidebar.
 *
 * Paths are taken verbatim from `@gravity-ui/icons` v2 — the library the
 * reference design (`new-agent-numeo-main/src/App.tsx`, deployed at
 * https://new-agent-numeo-mysardorr-7097s-projects.vercel.app) uses via
 * `@numeo-design/components`. Inlining lets us match the deploy pixel-for-pixel
 * without pulling a new npm dependency into the admin bundle.
 *
 * Conventions:
 *   - 16×16 viewBox, `fill="currentColor"` (gravity-ui glyphs are filled,
 *     not stroked — matches the sidebar on the reference deploy)
 *   - A few icons (e.g. Truck) don't have a gravity-ui equivalent and fall
 *     back to tabler / custom strokes — those are clearly flagged in comments.
 *   - Size can be overridden via the `size` prop; default is 16.
 */

interface IconProps {
    size?: number
    className?: string
}

const Svg = ({ size = 16, className, children }: IconProps & { children: React.ReactNode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
        {children}
    </svg>
)

// ── Group icons ──────────────────────────────────────────────────────────────

/**
 * Home — house glyph for the "Home" nav row (tmsHomeGroup). Figma spec:
 * https://www.figma.com/design/QDoa5u83nNciI95Vx3s39s/Spot?node-id=8654-215431.
 * Stroke-based like `Truck` — gravity-ui doesn't ship a matching house glyph,
 * so this falls back to the exact Figma paths instead (stroke swapped to
 * `currentColor`, the frame's background rect dropped).
 */
export const Home = ({ size = 16, className }: IconProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 25 25"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
    >
        <path d="M3.125 12.4897V15.1042C3.125 18.5417 3.125 20.2605 4.19271 21.3282C5.26042 22.3959 6.97917 22.3959 10.4167 22.3959H14.5833C18.0208 22.3959 19.7396 22.3959 20.8073 21.3282C21.875 20.2605 21.875 18.5417 21.875 15.1042V12.4897C21.875 10.7376 21.875 9.86258 21.5042 9.10425C21.1333 8.34591 20.4417 7.80841 19.0604 6.73341L16.9771 5.11362C14.826 3.44071 13.75 2.60425 12.5 2.60425C11.25 2.60425 10.174 3.44071 8.02292 5.11362L5.93958 6.73341C4.55729 7.80841 3.86667 8.34591 3.49583 9.10425C3.125 9.86258 3.125 10.7376 3.125 12.4897Z" />
        <path d="M15.625 17.7085C14.7917 18.3564 13.6979 18.7502 12.5 18.7502C11.3021 18.7502 10.2083 18.3564 9.375 17.7085" />
    </svg>
)

/**
 * Rocket — hand-rolled rocket glyph for the Onboarding row. `@gravity-ui/icons`
 * doesn't ship a rocket, and the reference design's own rocket
 * (`new-agent-numeo-main/src/App.tsx` lines 29–47) is drawn edge-to-edge:
 * the exhaust trail reaches x≈0.8 and the rocket nose reaches x≈15.5 in a
 * 16×16 viewBox. Compared to neighbours like `Rectangles4` (content in
 * x=1.5–14.5), the rocket's bounding box is ~3 px wider, so on the
 * collapsed rail the trail tip looks like it's "stuck to the left wall".
 *
 * Fix: use a symmetric `viewBox="-2 -2 20 20"` (2 units of padding on every
 * side) while keeping the rendered size at 16×16. Default `xMidYMid meet`
 * scales the 20-unit content to fit the 16-px frame, giving the rocket
 * ~2 px of breathing room on each side — the same visual rhythm as the
 * other gravity-ui glyphs.
 *
 * The reference's `clipPath` wrapper was dropped: it clipped to the full
 * viewBox (no-op) and its `id="rocket-clip"` collided across multiple
 * Rocket instances on the page.
 */
export const Rocket = ({ size = 16, className }: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="-2 -2 20 20" fill="none" className={className} aria-hidden="true">
        <path
            fill="currentColor"
            fillRule="evenodd"
            clipRule="evenodd"
            d="m6.993 5.628-.787-.157-1.118-.224a1.13 1.13 0 0 0-1.024.31L2.837 6.785a1.15 1.15 0 0 0-.285.474l1.02.185a6.19 6.19 0 0 1 4.985 4.985l.185 1.02c.178-.055.34-.152.474-.285l1.227-1.227c.268-.268.384-.652.31-1.024l-.224-1.118-.157-.787.567-.568 1.243-1.242A4.5 4.5 0 0 0 13.5 4.015V2.5h-1.515a4.5 4.5 0 0 0-3.182 1.318L7.561 5.061zM12 9.5l1.243-1.243A6 6 0 0 0 15 4.015V2.5A1.5 1.5 0 0 0 13.5 1h-1.515a6 6 0 0 0-4.242 1.757L6.5 4l-1.118-.224a2.63 2.63 0 0 0-2.379.72L1.777 5.724A2.65 2.65 0 0 0 1 7.598c0 .522.373.97.887 1.063l1.417.258a4.69 4.69 0 0 1 3.777 3.777l.258 1.417c.093.514.54.887 1.063.887.703 0 1.377-.28 1.875-.777l1.226-1.226a2.63 2.63 0 0 0 .72-2.38zm-8.06 2.571c-.311-.31-.76-.28-1.005-.036-.233.233-.423.658-.527 1.247a5 5 0 0 0-.05.366q.184-.019.377-.053c.596-.106 1.017-.296 1.24-.52.245-.244.275-.693-.036-1.004M5 11.011c-.873-.874-2.273-.89-3.126-.036C.777 12.07.802 14.094.837 14.712c.007.12.06.23.145.315a.5.5 0 0 0 .32.145c.622.032 2.652.046 3.734-1.035.853-.854.837-2.253-.036-3.126m6.78-5.73a.75.75 0 0 0-1.06-1.061l-1.5 1.5a.75.75 0 0 0 1.06 1.06z"
        />
    </svg>
)

/** Rectangles4 — 4-square grid (Dashboard / Fleet Overview / Safety Dashboard). */
export const Rectangles4 = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M3.5 3h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5m-2 .5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zm9-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5m-2 .5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zm-3 6.5h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5m-2-1.5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2zm7 1.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5m-2 .5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Magnifier — magnifying glass (Find Loads / Load Hub). */
export const Magnifier = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M11.5 7a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0m-.82 4.74a6 6 0 1 1 1.06-1.06l2.79 2.79a.75.75 0 1 1-1.06 1.06z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Sliders — two horizontal sliders (Manage Loads). */
export const Sliders = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M7.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m1.405.75a3.001 3.001 0 0 1-5.81 0H1.747a.75.75 0 0 1 0-1.5h1.348a3.001 3.001 0 0 1 5.81 0h5.345a.75.75 0 0 1 0 1.5zm-7.158 4.5h5.345a3.001 3.001 0 0 1 5.811 0h1.347a.75.75 0 1 1 0 1.5h-1.347a3.001 3.001 0 0 1-5.81 0H1.746a.75.75 0 0 1 0-1.5m8.25-.75a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3"
            clipRule="evenodd"
        />
    </Svg>
)

/** CircleLetterS — circle with letter S (Accounting). */
export const CircleLetterS = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M13.5 8a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0M8.054 4.502c-.708 0-1.34.18-1.811.558a1.95 1.95 0 0 0-.74 1.545c0 .764.474 1.265.94 1.559.457.287 1.007.448 1.448.547.462.102.844.191 1.119.341.227.125.274.224.274.376 0 .102-.04.217-.248.341-.224.135-.577.229-.982.229-.344 0-.682-.114-.952-.29-.282-.184-.42-.388-.457-.506a.75.75 0 1 0-1.43.452c.17.543.59 1 1.067 1.31a3.27 3.27 0 0 0 1.772.534c.602 0 1.24-.134 1.754-.443.53-.319.976-.864.976-1.627 0-.864-.51-1.394-1.055-1.692-.478-.26-1.056-.389-1.46-.478l-.053-.012c-.386-.086-.736-.202-.973-.352-.227-.142-.24-.236-.24-.29a.45.45 0 0 1 .18-.375c.134-.108.403-.227.87-.227.47 0 .742.11.9.218a.83.83 0 0 1 .316.41.75.75 0 0 0 1.407-.52A2.33 2.33 0 0 0 9.8 4.98c-.455-.31-1.037-.478-1.745-.478"
            clipRule="evenodd"
        />
    </Svg>
)

/** Shield — heraldic shield with check (Safety / Identity Protection). */
export const Shield = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="m3.003 4.702 4.22-2.025a1.8 1.8 0 0 1 1.554 0l4.22 2.025a.89.89 0 0 1 .503.8V6a8.55 8.55 0 0 1-3.941 7.201l-.986.631a1.06 1.06 0 0 1-1.146 0l-.986-.63A8.55 8.55 0 0 1 2.5 6v-.498c0-.341.196-.652.503-.8m3.57-3.377L2.354 3.35A2.39 2.39 0 0 0 1 5.502V6a10.05 10.05 0 0 0 4.632 8.465l.986.63a2.56 2.56 0 0 0 2.764 0l.986-.63A10.05 10.05 0 0 0 15 6v-.498c0-.918-.526-1.755-1.354-2.152l-4.22-2.025a3.3 3.3 0 0 0-2.852 0M8.47 9.97a.75.75 0 1 0 1.06 1.06c.575-.574 1.118-1.398 1.516-2.195.386-.772.704-1.653.704-2.335a.75.75 0 0 0-1.5 0c0 .318-.182.937-.546 1.665-.352.703-.809 1.379-1.234 1.805"
            clipRule="evenodd"
        />
    </Svg>
)

/** Persons — two people (Profiles group). */
export const Persons = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M5.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m0 1.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6m-3.029 2.886c-.777.54-.971 1.063-.971 1.306 0 .446.362.808.808.808h6.384a.81.81 0 0 0 .808-.808c0-.244-.194-.767-.971-1.306C7.792 9.875 6.719 9.5 5.5 9.5s-2.292.375-3.029.886M0 11.692C0 9.846 2.475 8 5.5 8c1.18 0 2.278.281 3.177.734A5.67 5.67 0 0 1 11.5 8c2.475 0 4.5 1.538 4.5 3.077A1.923 1.923 0 0 1 14.077 13h-3.483c-.416.604-1.113 1-1.902 1H2.308A2.31 2.31 0 0 1 0 11.692m10.991-.192h3.086c.234 0 .423-.19.423-.423 0-.103-.096-.472-.688-.89-.554-.393-1.375-.687-2.312-.687-.517 0-.999.09-1.42.236.526.534.854 1.146.911 1.764M12.5 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0M14 5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0"
            clipRule="evenodd"
        />
    </Svg>
)

/**
 * Truck (Fleet Management / Trucks / Fleet View).
 *
 * `@gravity-ui/icons` doesn't ship a truck glyph, so the reference design
 * inlines this tabler-icons truck verbatim (`new-agent-numeo-main/src/App.tsx`
 * lines 71, 110, 115). Kept on its own 24×24 viewBox so the silhouette stays
 * legible at 16×16.
 */
export const Truck = ({ size = 16, className }: IconProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
    >
        <path d="M5 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
        <path d="M15 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
        <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5" />
        <path d="M3 9l4 0" />
    </svg>
)

/**
 * ChartLine — line chart (Analytics / Reports / P&L).
 *
 * Same edge-to-edge framing problem as `Rocket`: gravity-ui's path goes
 * from x≈0.74 (`Q...0.737 7.203`) to x≈15.5, so the wave touches both
 * vertical walls of the 16×16 viewBox and looks "fatter" than its
 * neighbours on the collapsed rail. Symmetric `viewBox="-2 -2 20 20"`
 * adds 2 units of padding on every side, matching the visual rhythm of
 * `Rectangles4`, `Magnifier`, `Sliders`, etc.
 */
export const ChartLine = ({ size = 16, className }: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="-2 -2 20 20" fill="none" className={className} aria-hidden="true">
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M15.326 1.27a.75.75 0 0 1-.096 1.056l-3.674 3.062a2.75 2.75 0 0 1-2.55.522l-2.869-.86a1.25 1.25 0 0 0-1.214.285l-3.16 2.962A.75.75 0 1 1 .737 7.203l3.16-2.963a2.75 2.75 0 0 1 2.671-.628l2.868.86c.402.121.837.032 1.16-.236l3.674-3.062a.75.75 0 0 1 1.056.096m.113 6.185a.75.75 0 0 1-.393.984l-4.398 1.885a2.75 2.75 0 0 1-2.313-.068L6.186 9.182a1.25 1.25 0 0 0-1.238.068l-3.29 2.13a.75.75 0 0 1-.815-1.26l3.29-2.129a2.75 2.75 0 0 1 2.724-.15l2.149 1.073a1.25 1.25 0 0 0 1.051.031l4.398-1.884a.75.75 0 0 1 .984.394M1.25 12.5a.75.75 0 0 0 0 1.5h13.5a.75.75 0 0 0 0-1.5z"
            clipRule="evenodd"
        />
    </svg>
)

// ── Item icons ───────────────────────────────────────────────────────────────

/** Sparkles (AI Hub). */
export const Sparkles = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            d="M13 10a.75.75 0 0 1 .725.556 2.37 2.37 0 0 0 1.72 1.72.75.75 0 0 1 0 1.449 2.37 2.37 0 0 0-1.72 1.72.75.75 0 0 1-1.45 0 2.37 2.37 0 0 0-1.72-1.72.75.75 0 0 1 0-1.45 2.37 2.37 0 0 0 1.72-1.72l.043-.117A.75.75 0 0 1 13 10M7 0a1.5 1.5 0 0 1 1.48 1.253c.242 1.455.696 2.364 1.3 2.968.603.603 1.512 1.057 2.967 1.3a1.5 1.5 0 0 1 0 2.958c-1.455.243-2.364.697-2.968 1.3-.603.604-1.057 1.513-1.3 2.968a1.5 1.5 0 0 1-2.958 0c-.243-1.455-.697-2.364-1.3-2.968-.604-.603-1.513-1.057-2.968-1.3a1.5 1.5 0 0 1 0-2.958c1.455-.243 2.364-.697 2.968-1.3.603-.604 1.057-1.513 1.3-2.968l.028-.133A1.5 1.5 0 0 1 7 0m0 1.5C6.45 4.8 4.8 6.45 1.5 7c3.3.55 4.95 2.2 5.5 5.5.55-3.3 2.2-4.95 5.5-5.5C9.2 6.45 7.55 4.8 7 1.5"
        />
    </Svg>
)

/** AntennaSignal — antenna with signal arcs (Load Radar). */
export const AntennaSignal = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M5.56 4.403c.27.314.223.784-.015 1.123A3 3 0 0 0 5 7.25c0 .642.202 1.237.545 1.724.238.339.284.809.015 1.123-.27.315-.75.354-1.015.036A4.48 4.48 0 0 1 3.5 7.25c0-1.097.393-2.102 1.045-2.883.266-.318.745-.279 1.015.036m4.88 0c-.27.314-.223.784.015 1.123.344.487.545 1.082.545 1.724s-.201 1.237-.545 1.724c-.238.339-.284.809-.015 1.123.27.315.75.354 1.015.036A4.48 4.48 0 0 0 12.5 7.25a4.48 4.48 0 0 0-1.045-2.883c-.265-.318-.745-.279-1.015.036m1.953-2.278c-.27.315-.23.785.05 1.092A5.98 5.98 0 0 1 14 7.25c0 1.553-.59 2.968-1.558 4.033-.278.307-.319.777-.05 1.092.27.314.747.353 1.033.053A7.47 7.47 0 0 0 15.5 7.25c0-2.008-.79-3.832-2.075-5.178-.286-.3-.763-.261-1.032.053m-8.786 0c-.27-.314-.746-.353-1.032-.053A7.48 7.48 0 0 0 .5 7.25c0 2.008.79 3.832 2.075 5.178.286.3.763.261 1.032-.053.27-.315.23-.785-.05-1.092A5.98 5.98 0 0 1 2 7.25c0-1.553.59-2.968 1.558-4.033.278-.307.319-.777.05-1.092M8.75 8.55a1.5 1.5 0 1 0-1.5 0v5.701a.75.75 0 0 0 1.5 0z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Tray (Truck Availability). */
export const Tray = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M13.5 8.5h-2.388a3.422 3.422 0 0 1-6.224 0H2.5V11A1.5 1.5 0 0 0 4 12.5h8a1.5 1.5 0 0 0 1.5-1.5zm-2.204-4.757L13.251 7H10l-.136.545a1.921 1.921 0 0 1-3.728 0L6 7H2.75l1.954-3.257a.5.5 0 0 1 .428-.243h5.736a.5.5 0 0 1 .428.243M15 8.5v-.67a3 3 0 0 0-.428-1.543l-1.99-3.316A2 2 0 0 0 10.869 2H5.132a2 2 0 0 0-1.715.971l-1.99 3.316A3 3 0 0 0 1 7.831V11a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Person — single person (Drivers / Driver Pay). */
export const Person = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            d="M8 8.5c3.85 0 7 2.5 7 4.5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2c0-2 3.15-4.5 7-4.5M8 10c-1.61 0-3.064.526-4.092 1.234C2.798 12.001 2.5 12.733 2.5 13a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5c0-.267-.297-1-1.408-1.766C11.064 10.526 9.609 10 8 10m0-9a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7m0 1.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4"
        />
    </Svg>
)

/** PersonWorker — worker with hard hat (Contractors / Contractor Pay). */
export const PersonWorker = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M4.535 3A3.5 3.5 0 0 1 7.25.08v1.67a.75.75 0 1 0 1.5 0V.08A3.5 3.5 0 0 1 11.464 3h.286a.75.75 0 0 1 0 1.5h-.25a3.5 3.5 0 0 1-7 0h-.25a.75.75 0 0 1 0-1.5zM8 6.5a2 2 0 0 1-2-2h4a2 2 0 0 1-2 2m-5.5 6c0-.204.22-.809 1.32-1.459a6 6 0 0 1 .223-.125L5.01 13.5H3.5a1 1 0 0 1-1-1m4.114 1-1.179-3.142A9.2 9.2 0 0 1 8 10c.93 0 1.8.135 2.565.357L9.387 13.5zm4.375 0H12.5a1 1 0 0 0 1-1c0-.204-.22-.809-1.32-1.459a6 6 0 0 0-.223-.125zM8 8.5c-3.85 0-7 2-7 4A2.5 2.5 0 0 0 3.5 15h9a2.5 2.5 0 0 0 2.5-2.5c0-2-3.15-4-7-4"
            clipRule="evenodd"
        />
    </Svg>
)

/** Box — cardboard box (Trailers). */
export const Box = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M13.5 10.421V5.475l-2 .714V8.25a.75.75 0 0 1-1.5 0V6.725l-2.25.804v6.088l4.777-1.792a1.5 1.5 0 0 0 .973-1.404m-2.254-5.734 1.6-.571a2 2 0 0 0-.175-.104L9.499 2.427a1.5 1.5 0 0 0-1.197-.063l-.941.353 3.724 1.862q.09.045.16.108M5.444 3.435l3.878 1.94-2.273.811-3.805-1.903q.108-.063.23-.109zm.806 4.029L2.5 5.589v5.057a1.5 1.5 0 0 0 .83 1.342l2.92 1.46zM1 5.579c0-.436.094-.856.266-1.236a.75.75 0 0 1 .2-.37c.342-.54.855-.968 1.48-1.203L7.777.96a3 3 0 0 1 2.394.125l3.172 1.586A3 3 0 0 1 15 5.354v5.067a3 3 0 0 1-1.947 2.809l-4.828 1.81a3 3 0 0 1-2.395-.125l-3.172-1.586A3 3 0 0 1 1 10.646z"
            clipRule="evenodd"
        />
    </Svg>
)

/** PersonGear — person with gear (Brokers). */
export const PersonGear = (p: IconProps) => (
    <Svg {...p}>
        <g clipPath="url(#person-gear-clip)">
            <path
                fill="currentColor"
                fillRule="evenodd"
                d="M7 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4M7 8a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m-.5 1.299c0-.43-.36-.774-.785-.724C2.473 8.955 0 10.728 0 12.5A2.5 2.5 0 0 0 2.5 15h3.25a.75.75 0 0 0 0-1.5H2.5a1 1 0 0 1-1-1c0-.205.22-.809 1.32-1.459.765-.452 1.792-.813 2.969-.964.397-.051.711-.378.711-.778m8.002 1.89a2.22 2.22 0 0 1-1.66-2.138l-.68-.256a2.38 2.38 0 0 1-2.747.816l-.406.569c.57.788.57 1.87 0 2.657l.406.569a2.38 2.38 0 0 1 2.747.816l.68-.255a2.22 2.22 0 0 1 1.66-2.139zm-.112-2.396a.715.715 0 0 0-.418-.921l-1.798-.674a.865.865 0 0 0-1.114.506.87.87 0 0 1-1.32.395.884.884 0 0 0-1.23.2L7.434 9.805a.77.77 0 0 0 .179 1.076c.43.308.43.948 0 1.255a.77.77 0 0 0-.18 1.076l1.077 1.506a.884.884 0 0 0 1.23.2.87.87 0 0 1 1.32.395.865.865 0 0 0 1.114.506l1.798-.674a.715.715 0 0 0 .418-.92.715.715 0 0 1 .67-.966h.134a.81.81 0 0 0 .809-.809v-1.883a.81.81 0 0 0-.809-.808h-.135a.715.715 0 0 1-.669-.966m-1.833 2.659a1.052 1.052 0 1 1-2.103 0 1.052 1.052 0 0 1 2.103 0"
                clipRule="evenodd"
            />
        </g>
        <defs>
            <clipPath id="person-gear-clip">
                <path fill="#fff" d="M0 0h16v16H0z" />
            </clipPath>
        </defs>
    </Svg>
)

/** LayoutList (Dispatch Board). */
export const LayoutList = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M4 3.5h1.25v2H2.5V5A1.5 1.5 0 0 1 4 3.5m2.75 2v-2H12A1.5 1.5 0 0 1 13.5 5v.5zM2.5 7h2.75v2H2.5zm0 3.5v.5A1.5 1.5 0 0 0 4 12.5h1.25v-2zm4.25 0v2H12a1.5 1.5 0 0 0 1.5-1.5v-.5zM13.5 9V7H6.75v2zM1 5a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Route with two waypoints (Ongoing Loads). Exported as `RouteIcon` to avoid
 * clashing with `Route` from react-router. */
export const RouteIcon = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M12.25 5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5m0 1.5a2.75 2.75 0 0 1-2.646-2H4.577a.827.827 0 0 0-.238 1.619l7.753 2.326A2.327 2.327 0 0 1 11.423 13H6.397a2.751 2.751 0 1 1 0-1.5h5.026a.827.827 0 0 0 .238-1.619L3.908 7.556A2.327 2.327 0 0 1 4.577 3h5.027a2.751 2.751 0 1 1 2.646 3.5M5 12.25a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0"
            clipRule="evenodd"
        />
    </Svg>
)

/** Receipt — ribbon-edged receipt (Invoices). */
export const Receipt = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M6.3 2 3.9.5 2 2v13.5l1.5-.776L4.9 14l2.4 1.5L9.7 14l2.4 1.5L14 14V.5l-1.5.776L11.1 2 8.7.5zm2.4.269L7.095 3.272l-.795.497-.795-.497-1.504-.94-.501.395v10.308l.71-.367.76-.393.725.453L7.3 13.731l1.605-1.003.795-.497.795.497 1.504.94.501-.395V2.965l-.71.367-.76.393-.725-.453zM5 6.5a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 5 6.5m.75 2.25a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Dollar — circle-dollar glyph (Expenses / Prepayments). */
export const Dollar = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M13.5 8a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0M8.75 4.25a.75.75 0 0 0-1.5 0v.339a2.5 2.5 0 0 0-1.007.47 1.95 1.95 0 0 0-.74 1.546c0 .764.474 1.265.94 1.559.456.287 1.007.448 1.448.547.462.102.843.191 1.118.341.228.125.275.224.275.376 0 .102-.04.217-.248.341-.224.135-.577.229-.982.229-.344 0-.683-.114-.953-.29-.281-.184-.42-.388-.457-.506a.75.75 0 1 0-1.43.452c.171.543.591 1 1.068 1.31.284.185.612.335.968.429v.357a.75.75 0 0 0 1.5 0v-.313c.375-.067.74-.19 1.058-.382.53-.319.976-.864.976-1.627 0-.864-.51-1.394-1.055-1.692-.478-.26-1.056-.389-1.46-.478l-.053-.012c-.386-.086-.736-.202-.973-.352-.227-.142-.24-.236-.24-.29a.45.45 0 0 1 .18-.375c.134-.108.403-.227.87-.227.47 0 .742.11.9.218a.83.83 0 0 1 .316.41.75.75 0 0 0 1.407-.52 2.33 2.33 0 0 0-.878-1.13 2.7 2.7 0 0 0-1.048-.417z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Wallet — credit-card glyph (Factoring / Billing). `@gravity-ui/icons` has
 * no wallet, so we use `credit-card` which matches the same financial context. */
export const Wallet = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M12.5 4h-9A1.5 1.5 0 0 0 2 5.5h12A1.5 1.5 0 0 0 12.5 4M2 10.5V7h12v3.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 10.5m1.5-8a3 3 0 0 0-3 3v5a3 3 0 0 0 3 3h9a3 3 0 0 0 3-3v-5a3 3 0 0 0-3-3zM4.25 9a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5z"
            clipRule="evenodd"
        />
    </Svg>
)

/** ClipboardCheck — check-shape glyph (Inspections / DOT Inspections).
 * gravity-ui doesn't ship `clipboard-check`; `check-shape` is the closest
 * "approved / inspected" visual and keeps these rows distinct from Compliance
 * (which uses `FileCheck`). */
export const ClipboardCheck = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M6.943 8.703 4.301 6.3a.25.25 0 0 0-.355.02L2.299 8.171a.25.25 0 0 0 .023.355l4.785 4.147a.25.25 0 0 0 .36-.032L13.29 5.36a.25.25 0 0 0-.03-.343l-1.856-1.65a.25.25 0 0 0-.364.034zM6.75 6.5l3.104-4.017a1.75 1.75 0 0 1 2.547-.238l1.857 1.651a1.75 1.75 0 0 1 .204 2.401L8.637 13.58a1.75 1.75 0 0 1-2.512.229L1.339 9.66a1.75 1.75 0 0 1-.162-2.486l1.647-1.852A1.75 1.75 0 0 1 5.31 5.19z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Wrench (Maintenance / Equipment). */
export const Wrench = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="m8.581 9.298.776.143q.311.059.643.059A3.5 3.5 0 0 0 13.5 6q0-.177-.027-.352l-1.39 1.39a1.58 1.58 0 0 1-1.114.462A2.47 2.47 0 0 1 8.5 5.03c0-.417.166-.817.461-1.112l1.39-1.39A2.4 2.4 0 0 0 10 2.5a3.5 3.5 0 0 0-3.441 4.143l.143.776-3.813 3.813a1.329 1.329 0 0 0 1.879 1.879zm3.817-6.787a.795.795 0 0 0-.411-1.018C11.87 1.432 11.014 1 10 1a5 5 0 0 0-4.916 5.916l-3.256 3.256a2.828 2.828 0 1 0 4 4l3.256-3.256Q9.53 11 10 11a5 5 0 0 0 5-5c0-1.014-.432-1.87-.493-1.987l-.014-.027a.795.795 0 0 0-1.273-.207l-2.198 2.2a.07.07 0 0 1-.053.021.97.97 0 0 1-.969-.97q0-.03.022-.052L12.22 2.78a.8.8 0 0 0 .178-.27"
            clipRule="evenodd"
        />
    </Svg>
)

/** FileCheck (Compliance / DOT Inspections). */
export const FileCheck = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M11 13.5H5A1.5 1.5 0 0 1 3.5 12V4A1.5 1.5 0 0 1 5 2.5h2.757a1.5 1.5 0 0 1 1.061.44l3.243 3.242a1.5 1.5 0 0 1 .439 1.06V12a1.5 1.5 0 0 1-1.5 1.5m2.121-8.379A3 3 0 0 1 14 7.243V12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V4a3 3 0 0 1 3-3h2.757a3 3 0 0 1 2.122.879zM10.85 7.65a.75.75 0 1 0-1.2-.9L7.469 9.658 6.28 8.47a.75.75 0 0 0-1.06 1.06l1.8 1.8a.75.75 0 0 0 1.13-.08z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Folder (Company Files). */
export const Folder = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="m6.44 4.06.439.44H12.5A1.5 1.5 0 0 1 14 6v5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11V4.5A1.5 1.5 0 0 1 3.5 3h1.257a1.5 1.5 0 0 1 1.061.44zM.5 4.5a3 3 0 0 1 3-3h1.257a3 3 0 0 1 2.122.879L7.5 3h5a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3zm4.25 2a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5z"
            clipRule="evenodd"
        />
    </Svg>
)

/** GasPump — `@gravity-ui/icons` has no pump/fuel glyph; the closest match is
 * `flame` which reads as "fuel / combustion". */
export const GasPump = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="m6.452 6.864 1.13-2.173a32 32 0 0 1 1.872-3.095c.964 1.045 1.906 2.3 2.612 3.622.748 1.402 1.184 2.789 1.184 4.032 0 1.427-.904 2.83-2.153 3.613q.088-.398.09-.863c0-1.255-.674-2.336-1.143-2.963a9 9 0 0 0-1.01-1.125l-.024-.02-.008-.008L9 7.88l-.001-.001C8.996 7.88 8.996 7.878 8 9a7 7 0 0 0 .984 1.133c.37.534.704 1.2.704 1.867 0 .77-.313 1.276-.618 1.587-.159.162-.279.38-.314.6a.8.8 0 0 0 0 .264q.017.095.06.182c.113.225.343.37.594.35 2.836-.235 5.34-2.87 5.34-5.733 0-3.149-2.177-6.538-4.357-8.845A1.3 1.3 0 0 0 9.435 0 1.32 1.32 0 0 0 8.35.556 34 34 0 0 0 6.25 4l-.955-1.337-.016-.022a.986.986 0 0 0-1.573.004C2.62 4.123 1.25 6.249 1.25 9.25c0 2.863 2.504 5.498 5.34 5.733.25.02.481-.125.593-.35a.7.7 0 0 0 .06-.182.8.8 0 0 0 .001-.263 1.15 1.15 0 0 0-.314-.601c-.305-.31-.617-.817-.617-1.587 0-.666.333-1.333.703-1.867l.09-.128C7.544 9.405 8 9 8 9l-.997-1.12H7l-.003.003-.008.007-.024.021-.073.07a9 9 0 0 0-.937 1.056c-.47.626-1.143 1.707-1.143 2.962 0 .31.033.598.09.863C3.654 12.08 2.75 10.677 2.75 9.25c0-2.171.847-3.812 1.745-5.126l.534.748zM8 9l.997-1.121L8 6.993l-.997.886z"
            clipRule="evenodd"
        />
    </Svg>
)

/** FileText — document with text lines (IFTA). */
export const FileText = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M5 13.5h6a1.5 1.5 0 0 0 1.5-1.5V7.243a1.5 1.5 0 0 0-.44-1.061L8.819 2.939a1.5 1.5 0 0 0-1.06-.439H5A1.5 1.5 0 0 0 3.5 4v8A1.5 1.5 0 0 0 5 13.5m9-6.257a3 3 0 0 0-.879-2.122L9.88 1.88A3 3 0 0 0 7.757 1H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3zM5 8.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 5 8.25m.75 2.25a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5z"
            clipRule="evenodd"
        />
    </Svg>
)

/** AlertTriangle — triangle with exclamation (Drug & Alcohol / Accidents). */
export const AlertTriangle = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M7.134 2.994 2.217 11.5a1 1 0 0 0 .866 1.5h9.834a1 1 0 0 0 .866-1.5L8.866 2.993a1 1 0 0 0-1.732 0m3.03-.75c-.962-1.665-3.366-1.665-4.329 0L.918 10.749c-.963 1.666.24 3.751 2.165 3.751h9.834c1.925 0 3.128-2.085 2.164-3.751zM8 5a.75.75 0 0 1 .75.75v2a.75.75 0 0 1-1.5 0v-2A.75.75 0 0 1 8 5m1 5.75a1 1 0 1 1-2 0 1 1 0 0 1 2 0"
            clipRule="evenodd"
        />
    </Svg>
)

/** HelpCircle — circle with question mark (Help & FAQ). */
export const HelpCircle = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M8 13.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14M6.44 4.54c.43-.354.994-.565 1.56-.565 1.217 0 2.34.82 2.34 2.14 0 .377-.078.745-.298 1.1-.208.339-.513.614-.875.867-.217.153-.325.257-.379.328-.038.052-.038.07-.038.089a.75.75 0 0 1-1.5 0c0-.794.544-1.286 1.057-1.645.28-.196.4-.332.458-.426a.54.54 0 0 0 .075-.312c0-.3-.244-.641-.84-.641a1 1 0 0 0-.608.223c-.167.138-.231.287-.231.418a.75.75 0 0 1-1.5 0c0-.674.345-1.22.78-1.577M8 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2"
            clipRule="evenodd"
        />
    </Svg>
)

/** SettingsGear — gravity-ui `gear` (Settings). */
export const SettingsGear = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M7.199 2H8.8a.2.2 0 0 1 .2.2c0 1.808 1.958 2.939 3.524 2.034a.2.2 0 0 1 .271.073l.802 1.388a.2.2 0 0 1-.073.272c-1.566.904-1.566 3.164 0 4.069a.2.2 0 0 1 .073.271l-.802 1.388a.2.2 0 0 1-.271.073C10.958 10.863 9 11.993 9 13.8a.2.2 0 0 1-.199.2H7.2a.2.2 0 0 1-.2-.2c0-1.808-1.958-2.938-3.524-2.034a.2.2 0 0 1-.272-.073l-.8-1.388a.2.2 0 0 1 .072-.271c1.566-.905 1.566-3.165 0-4.07a.2.2 0 0 1-.073-.27l.801-1.389a.2.2 0 0 1 .272-.072C5.042 5.138 7 4.007 7 2.199c0-.11.089-.199.199-.199M5.5 2.2c0-.94.76-1.7 1.699-1.7H8.8c.94 0 1.7.76 1.7 1.7a.85.85 0 0 0 1.274.735 1.7 1.7 0 0 1 2.32.622l.802 1.388c.469.813.19 1.851-.622 2.32a.85.85 0 0 0 0 1.472 1.7 1.7 0 0 1 .622 2.32l-.802 1.388a1.7 1.7 0 0 1-2.32.622.85.85 0 0 0-1.274.735c0 .939-.76 1.7-1.699 1.7H7.2a1.7 1.7 0 0 1-1.699-1.7.85.85 0 0 0-1.274-.735 1.7 1.7 0 0 1-2.32-.622l-.802-1.388a1.7 1.7 0 0 1 .622-2.32.85.85 0 0 0 0-1.471 1.7 1.7 0 0 1-.622-2.32l.801-1.389a1.7 1.7 0 0 1 2.32-.622A.85.85 0 0 0 5.5 2.2m4 5.8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0M11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0"
            clipRule="evenodd"
        />
    </Svg>
)

/**
 * LayoutSideContentLeft / LayoutSideContentRight — sidebar collapse toggle.
 * Two distinct glyphs (not one rotated chevron) matching the prototype's
 * two-panel icon convention: "content on the left" while expanded, "content
 * on the right" while collapsed.
 */
export const LayoutSideContentLeft = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M6 3.5h6A1.5 1.5 0 0 1 13.5 5v6a1.5 1.5 0 0 1-1.5 1.5H6zm-1.5 0H4A1.5 1.5 0 0 0 2.5 5v6A1.5 1.5 0 0 0 4 12.5h.5zM1 5a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3z"
            clipRule="evenodd"
        />
    </Svg>
)

export const LayoutSideContentRight = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M10 3.5H4A1.5 1.5 0 0 0 2.5 5v6A1.5 1.5 0 0 0 4 12.5h6zm1.5 0h.5A1.5 1.5 0 0 1 13.5 5v6a1.5 1.5 0 0 1-1.5 1.5h-.5zM15 5a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3z"
            clipRule="evenodd"
        />
    </Svg>
)

/** Sun — shown while the app is in light mode (click to switch to dark). */
export const Sun = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M8 3a.75.75 0 0 1-.75-.75V.75a.75.75 0 0 1 1.5 0v1.5A.75.75 0 0 1 8 3m0 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8m-.75 3.25a.75.75 0 0 0 1.5 0v-1.5a.75.75 0 0 0-1.5 0zM13 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 13 8M.75 7.25a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5zm10.786-2.786a.75.75 0 0 1 0-1.06l1.06-1.06a.75.75 0 0 1 1.06 1.06l-1.06 1.06a.75.75 0 0 1-1.06 0m-9.193 8.132a.75.75 0 0 0 1.06 1.06l1.062-1.06a.75.75 0 0 0-1.061-1.06zm9.193-1.06a.75.75 0 0 1 1.06 0l1.06 1.06a.75.75 0 0 1-1.06 1.06l-1.06-1.06a.75.75 0 0 1 0-1.06M3.404 2.343a.75.75 0 0 0-1.06 1.06l1.06 1.061a.75.75 0 1 0 1.06-1.06z"
            clipRule="evenodd"
        />
    </Svg>
)

/** ChevronDown — used for collapsible group arrows. */
export const ChevronDown = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M2.47 5.47a.75.75 0 0 1 1.06 0L8 9.94l4.47-4.47a.75.75 0 1 1 1.06 1.06l-5 5a.75.75 0 0 1-1.06 0l-5-5a.75.75 0 0 1 0-1.06"
            clipRule="evenodd"
        />
    </Svg>
)

/** Bell — used for the Notifications footer button. */
export const Bell = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="m4.665 7.822.62-3.096a2.77 2.77 0 0 1 5.43 0l.62 3.096a4.8 4.8 0 0 0 1.305 2.44l.194.193a.567.567 0 0 1-.273.953l-.821.19a16.6 16.6 0 0 1-7.48 0l-.82-.19a.567.567 0 0 1-.274-.953l.194-.193a4.77 4.77 0 0 0 1.305-2.44m-1.47-.294.619-3.096a4.27 4.27 0 0 1 8.372 0l.62 3.096c.126.634.438 1.216.895 1.673l.194.194a2.066 2.066 0 0 1-.997 3.475l-.821.19q-1.053.24-2.12.358a2 2 0 0 1-3.913 0 18 18 0 0 1-2.12-.359l-.822-.19a2.067 2.067 0 0 1-.997-3.474L2.3 9.2c.457-.457.769-1.04.895-1.673"
            clipRule="evenodd"
        />
    </Svg>
)

/** Envelope — used for the Email nav item. */
export const Envelope = (p: IconProps) => (
    <Svg {...p}>
        <path
            d="M2 4.5A1.5 1.5 0 0 1 3.5 3h9A1.5 1.5 0 0 1 14 4.5v7A1.5 1.5 0 0 1 12.5 13h-9A1.5 1.5 0 0 1 2 11.5v-7Z"
            stroke="currentColor"
            strokeWidth="1.3"
        />
        <path d="M2 5.5 8 9.5l6-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </Svg>
)

/** Chat bubble — the Box Truck SMS conversations with drivers. */
export const ChatBubble = (p: IconProps) => (
    <Svg {...p}>
        <path
            d="M14 9.5A2.5 2.5 0 0 1 11.5 12H6l-3 2.5V4.5A2.5 2.5 0 0 1 5.5 2h6A2.5 2.5 0 0 1 14 4.5v5Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
        />
    </Svg>
)

/** Moon — shown while the app is in dark mode (click to switch to light). */
export const Moon = (p: IconProps) => (
    <Svg {...p}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M8 13.5a5.5 5.5 0 0 0 2.263-10.514 5.5 5.5 0 0 1-7.278 7.278A5.5 5.5 0 0 0 8 13.5M1.045 8.795a7.001 7.001 0 1 0 7.75-7.75l-.028-.003A7 7 0 0 0 8 1c-.527 0-.59.842-.185 1.18a4 4 0 0 1 .342.322A4 4 0 1 1 2.18 7.814C1.842 7.41 1 7.474 1 8a7 7 0 0 0 .045.794"
            clipRule="evenodd"
        />
    </Svg>
)
